/**
 * The Monday-start 7-day week model — mirrored VERBATIM from the carve's `routes/dispatch.tsx`
 * (`getMondayOfWeek` / `addDays`) so the SPA's prev/next/today navigation resolves IDENTICALLY to
 * the server (the load-bearing legacy contract — D-03). The server is still authoritative: the
 * coarse `GET /api/v1/dispatch?week=` resolves the canonical Monday + 7-day span and the SPA
 * renders THAT. This module only computes the `week=` query value for the prev / next / today nav
 * (a pure date string, no UI state).
 *
 * Monday-start (NOT Sunday-start): any day resolves to that week's Monday; Sunday belongs to the
 * PRECEDING Monday's week.
 */

/** Monday of the week containing `dateStr` (Monday-start). */
export function getMondayOfWeek(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const dow = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().slice(0, 10);
}

/** YYYY-MM-DD `n` days from `dateStr`. */
export function addDays(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Calendar-real YYYY-MM-DD (mirrors the carve isValidDate). */
export function isValidDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const [y, m, day] = s.split('-').map(Number);
  return d.getUTCFullYear() === y && d.getUTCMonth() + 1 === m && d.getUTCDate() === day;
}

/** HH:MM 24-hour (mirrors the carve isValidTime). */
export function isValidTime(s: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(s)) return false;
  const [h, m] = s.split(':').map(Number);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

/** Today as a YYYY-MM-DD string (UTC, matching the server's `new Date().toISOString()`). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The Monday `week=` value for the week BEFORE `weekStart`. */
export function prevWeek(weekStart: string): string {
  return addDays(getMondayOfWeek(weekStart), -7);
}

/** The Monday `week=` value for the week AFTER `weekStart`. */
export function nextWeek(weekStart: string): string {
  return addDays(getMondayOfWeek(weekStart), 7);
}

/** The Monday `week=` value for the week containing today. */
export function thisWeek(): string {
  return getMondayOfWeek(todayIso());
}

/** The 7 YYYY-MM-DD days (Monday → Sunday) of the week starting at `weekStart`. */
export function weekDays(weekStart: string): string[] {
  const monday = getMondayOfWeek(weekStart);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}
